const defaultTheme = require("tailwindcss/defaultTheme");
const { COLORS, COLOR_ALIASES, COLOR_EXTENSIONS } = require("./colors");
const { DEFAULT_THEME } = require("./themes");

/**
 * To avoid spacing growing to much as font size grows we keep spacing clamped for margins, paddings and gaps.
 */
const clampedSpacing = {
  0: "0px",
  px: "1px",
  0.5: "clamp(min(2px, 0.125rem), 0.125rem, 4px)",
  1: "clamp(min(4px, 0.25rem), 0.25rem, 8px)",
  1.5: "clamp(min(6px, 0.375rem), 0.375rem, 10px)",
  2: "clamp(min(8px, 0.5rem), 0.5rem, 12px)",
  2.5: "clamp(min(10px, 0.625rem), 0.625rem, 15px)",
  3: "clamp(min(12px, 0.75rem), 0.75rem, 16px)",
  3.5: "clamp(min(14px, 0.875rem), 0.875rem, 18px)",
  4: "clamp(min(16px, 1rem), 1rem, 20px)",
  5: "clamp(min(20px, 1.25rem), 1.25rem, 24px)",
  6: "clamp(min(24px, 1.5rem), 1.5rem, 32px)",
  7: "clamp(min(28px, 1.75rem), 1.75rem, 36px)",
  8: "clamp(min(32px, 2rem), 2rem, 40px)",
  9: "clamp(min(36px, 2.25rem), 2.25rem, 44px)",
  10: "clamp(min(40px, 2.5rem), 2.5rem, 48px)",
  11: "clamp(min(44px, 2.75rem), 2.75rem, 52px)",
  12: "clamp(min(48px, 3rem), 3rem, 64px)",
  14: "clamp(min(56px, 3.5rem), 3.5rem, 72px)",
  16: "clamp(min(64px, 4rem), 4rem, 80px)",
  20: "clamp(min(80px, 5rem), 5rem, 104px)",
  24: "clamp(min(96px, 6rem), 6rem, 120px)",
  28: "clamp(min(112px, 7rem), 7rem, 140px)",
  32: "clamp(min(128px, 8rem), 8rem, 160px)",
  36: "clamp(min(144px, 9rem), 9rem, 180px)",
  40: "clamp(min(160px, 10rem), 10rem, 200px)",
  44: "clamp(min(176px, 11rem), 11rem, 220px)",
  48: "clamp(min(192px, 12rem), 12rem, 240px)",
  52: "clamp(min(208px, 13rem), 13rem, 260px)",
  56: "clamp(min(224px, 14rem), 14rem, 280px)",
  60: "clamp(min(240px, 15rem), 15rem, 300px)",
  64: "clamp(min(256px, 16rem), 16rem, 320px)",
  72: "clamp(min(288px, 18rem), 18rem, 360px)",
  80: "clamp(min(320px, 20rem), 20rem, 400px)",
  96: "clamp(min(384px, 24rem), 24rem, 480px)",
};

const SCREEN_BREAKPOINTS = {
  xs: { max: "480px" },
  sm: { min: "481px" },
  md: { min: "768px" },
  lg: { min: "1024px" },
};

/** @type {import('tailwindcss').Config} */
module.exports = {
  darkMode: ["class", '[data-mode="dark"]'],
  theme: {
    fontSize: {
      xs: ["0.75rem", { lineHeight: 1.5 }],
      sm: ["0.875rem", { lineHeight: 1.5 }],
      base: ["1rem", { lineHeight: 1.5 }],
      lg: ["1.125rem", { lineHeight: 1.5 }],
      xl: ["1.25rem", { lineHeight: 1.3 }],
      "2xl": ["1.5rem", { lineHeight: 1.3 }],
      "3xl": ["1.75rem", { lineHeight: 1.3 }],
      "4xl": ["2rem", { lineHeight: 1.3 }],
      "5xl": ["2.25rem", { lineHeight: 1.3 }],
      "6xl": ["2.5rem", { lineHeight: 1.3 }],
      "7xl": ["2.75rem", { lineHeight: 1.2 }],
    },
    fontWeight: {
      normal: "400",
      medium: "500",
      bold: "700",
      heavy: "900",
    },
    lineHeight: {
      none: "1",
      slim: "1.20",
      tight: "1.25",
      snug: "1.30",
      cozy: "1.40",
      normal: "1.5",
    },
    spacing: {
      ...defaultTheme.spacing,
    },
    margin: {
      auto: "auto",
      ...clampedSpacing,
    },
    padding: clampedSpacing,
    gap: clampedSpacing,
    letterSpacing: {
      tighter: "-0.02em",
      tight: "-0.01em",
      normal: "0",
      wide: "0.01em",
      wider: "0.02em",
      widest: "0.1em",
    },
    borderRadius: {
      none: "0px",
      xs: "2px",
      sm: "4px",
      DEFAULT: "4px",
      md: "6px",
      lg: "8px",
      xl: "12px",
      "2xl": "16px",
      "3xl": "24px",
      "4xl": "32px",
      full: "9999px",
    },
    colors: {
      ...COLORS,
      primary: `var(--color-primary, ${DEFAULT_THEME.primary})`, // green-600
      primaryDarker: `var(--color-primary-darker, ${DEFAULT_THEME.primaryDarker})`, // green-800
      componentBackground: `var(--color-component-background, ${DEFAULT_THEME.componentBackground})`, // green-50
      componentBackgroundDarker: `var(--color-component-background-darker, ${DEFAULT_THEME.componentBackgroundDarker})`, // green-200
      background: `var(--color-background, ${DEFAULT_THEME.background})`, // sephia
      brand: `var(--color-brand, ${COLORS.orange.brand})`, // orange-brand
    },
    screens: SCREEN_BREAKPOINTS,
    fontFamily: {
      default: ["FilsonPro", "sans-serif"],
      sans: ["FilsonPro", "sans-serif"],
      heading: ["FilsonPro", "sans-serif"],
      price: ["FilsonProPrice", "sans-serif"],
    },
    extend: {
      spacing: {
        small: "10px",
        medium: "15px",
        large: "20px",
      },
      boxShadow: {
        base: "0 0 15px 0 rgba(0, 0, 0, 0.05)",
        button: `2px 2px 8px 0px color-mix(in srgb, ${COLORS.green[800]} 16%, transparent)`,
      },
      containers: {
        "media-sm": SCREEN_BREAKPOINTS.sm.min,
        "media-md": SCREEN_BREAKPOINTS.md.min,
        "media-lg": SCREEN_BREAKPOINTS.lg.min,
      },
      colors: {
        ...COLOR_EXTENSIONS,
        ...COLOR_ALIASES,
      },
      keyframes: {
        slideInRight: {
          "0%": { transform: "translateX(100%)", display: "none" },
          "100%": { transform: "translateX(0)", display: "block" },
        },
        slideOutRight: {
          "0%": { transform: "translateX(0)", display: "block" },
          "100%": { transform: "translateX(100%)", display: "none" },
        },
        slideInLeft: {
          "0%": { transform: "translateX(-100%)", display: "none" },
          "100%": { transform: "translateX(0)", display: "block" },
        },
        slideOutLeft: {
          "0%": { transform: "translateX(0)", display: "block" },
          "100%": { transform: "translateX(-100%)", display: "none" },
        },
        slideInBottom: {
          "0%": { transform: "translateY(100%)", display: "none" },
          "100%": { transform: "translateY(0)", display: "block" },
        },
        slideOutBottom: {
          "0%": { transform: "translateY(0)", display: "block" },
          "100%": { transform: "translateY(100%)", display: "none" },
        },
        fadeIn: {
          "0%": { opacity: 0, scale: 0.95, display: "none" },
          "100%": { opacity: 1, scale: 1, display: "block" },
        },
        fadeOut: {
          "0%": { opacity: 1, scale: 1, display: "block" },
          "100%": { opacity: 0, scale: 0.75, display: "none" },
        },
      },
      animation: {
        "slide-in-right": "slideInRight 300ms cubic-bezier(0.25, 0.1, 0.26, 1)",
        "slide-out-right":
          "slideOutRight 300ms cubic-bezier(0.25, 0.1, 0.26, 1)",
        "slide-in-left": "slideInLeft 300ms cubic-bezier(0.25, 0.1, 0.26, 1)",
        "slide-out-left": "slideOutLeft 300ms cubic-bezier(0.25, 0.1, 0.26, 1)",
        "slide-in-bottom":
          "slideInBottom 300ms cubic-bezier(0.25, 0.1, 0.26, 1)",
        "slide-out-bottom":
          "slideOutBottom 300ms cubic-bezier(0.25, 0.1, 0.26, 1)",
        "fade-in": "fadeIn 200ms ease-in-out",
        "fade-out": "fadeOut 200ms ease-in-out",
      },
    },
  },
};
